import { useEffect, useState } from "react"
import { createUseStyles } from 'react-jss'
import { useAppSelector, useAppDispatch } from "../redux/hooks"
import { EditPayDeviationModel, PayDeviation, EmployeeAndDateRangeFilter } from "../redux/types"
import Button from '../components/Button'
import FullScreenLoadingIndicator from "../components/FullScreenLoadingIndicator"
import Card from "../components/Card"
import ConfirmModal from "../components/ConfirmModal"
import { pageIndexChanged, filterChanged, errorHandled } from "../redux/slices/payDeviationSlice"
import FilterComponent from "../components/EmployeeAndDateRangeFilter"
import useDidUpdateEffect from "../hooks/useDidUpdateEffect"
import { useTranslation } from "react-i18next"
import usePayDeviationActions from "../hooks/usePayDeviationActions"
import PayDeviationList from "../components/PayDeviationList/PayDeviationList"
import EditPayDeviationModal from "../components/EditPayDeviationModal"
import ErrorModal from "../components/ErrorModal"
import { useNavigate } from "react-router-dom"

const Deviations = () => {
    const styles = useStyles()
    const { t } = useTranslation(['deviations', 'common']);
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const [confirmDeleteModalVisible, setConfirmDeleteModalVisible] = useState<boolean>(false)
    const [addModalVisible, setAddModalVisible] = useState<boolean>(false)
    const [editModalVisible, setEditModalVisible] = useState<boolean>(false)
    const [errorModalVisible, setErrorModalVisible] = useState<boolean>(false)

    const [editedPayDeviation, setEditedPayDeviation] = useState<PayDeviation>()
    const [deleteCandidate, setDeleteCandidate] = useState<PayDeviation>()

    const { getPayDeviations, createPayDeviation, deletePayDeviation, updatePayDeviation } = usePayDeviationActions()
    const loading = useAppSelector(state => state.payDeviations.loading)
    const deviationsOnPage = useAppSelector(state => state.payDeviations.deviationsOnPage)
    const totalDeviationCount = useAppSelector(state => state.payDeviations.totalCount)
    const pageIndex = useAppSelector(state => state.payDeviations.pageIndex)
    const pageSize = useAppSelector(state => state.payDeviations.pageSize)
    const filter = useAppSelector(state => state.payDeviations.filter)
    const error = useAppSelector(state => state.payDeviations.error)

    useEffect(() => {
        if (deviationsOnPage.length === 0)
            getPayDeviations(filter?.employeeId ?? undefined, filter?.fromDate ?? undefined, filter?.toDate ?? undefined, pageIndex, pageSize)
    }, [])

    useDidUpdateEffect(() => {
        getPayDeviations(filter?.employeeId ?? undefined, filter?.fromDate ?? undefined, filter?.toDate ?? undefined, pageIndex, pageSize)
    }, [filter, pageIndex])

    useDidUpdateEffect(() => {
        if (error) {
            setErrorModalVisible(true)
        }
    }, [error])

    const handleError = () => {
        dispatch(errorHandled())
        setErrorModalVisible(false)
    }

    const onAddClick = () => {
        setAddModalVisible(true)
    }

    const handleAdded = (deviation: EditPayDeviationModel) => {
        createPayDeviation(deviation, () => {
            getPayDeviations(filter?.employeeId ?? undefined, filter?.fromDate ?? undefined, filter?.toDate ?? undefined, pageIndex, pageSize)
        })
        setAddModalVisible(false)
    }

    const onEditClick = (deviation: PayDeviation) => {
        setEditedPayDeviation(deviation)
        setEditModalVisible(true)
    }

    const handleEdited = (editModel: EditPayDeviationModel) => {
        if (editedPayDeviation) {
            updatePayDeviation(editedPayDeviation.id, editModel)
        }
        setEditedPayDeviation(undefined)
        setEditModalVisible(false)
    }

    const handleEditCancelled = () => {
        setEditedPayDeviation(undefined)
        setEditModalVisible(false)
    }

    const onDeleteClick = (deviation: PayDeviation) => {
        setDeleteCandidate(deviation)
        setConfirmDeleteModalVisible(true)
    }

    const onDeviationClick = (deviation: PayDeviation) => {
        navigate(`/deviations/${deviation.id}`)
    }

    const handleDeleteConfirmed = () => {
        if (deleteCandidate) {
            deletePayDeviation(deleteCandidate)
            setDeleteCandidate(undefined)
        }
        setConfirmDeleteModalVisible(false)
    }

    const onPageChange = (pageIndex: number) => {
        dispatch(pageIndexChanged(pageIndex))
    }

    const handleFilterChange = (filter: EmployeeAndDateRangeFilter) => {
        dispatch(filterChanged(filter))
    }

    return (
        <>
        <Card className={styles.card}>
            <h2>
                {t('title')}
            </h2>
        </Card>
        <Card>
            <div className={styles.buttonContainer}>
                <Button onClick={onAddClick} title={t('addDeviation', {ns: 'deviations'})} className={styles.button} />
            </div>
            <div className={styles.filterContainer}>
                <FilterComponent
                    initialFilterValue={filter ?? undefined}
                    onFilterChange={handleFilterChange}
                />
            </div>
            {deviationsOnPage &&
            <PayDeviationList
                deviationsOnPage={deviationsOnPage}
                pageIndex={pageIndex}
                pageSize={pageSize}
                totalCount={totalDeviationCount}
                onDeviationClick={onDeviationClick}
                onEditClick={onEditClick}
                onDeleteClick={onDeleteClick}
                onPageChange={onPageChange}
            />}
            <EditPayDeviationModal
                key='addModal'
                visible={addModalVisible}
                onCancelClick={() => setAddModalVisible(false)}
                onSaveClick={handleAdded}
            />
            <EditPayDeviationModal
                key='editModal'
                initialValue={editedPayDeviation}
                visible={editModalVisible}
                onCancelClick={handleEditCancelled}
                onSaveClick={handleEdited}
            />
            <ConfirmModal
                visible={confirmDeleteModalVisible}
                confirmText={t('confirmDelete', {ns: 'deviations'})}
                onConfirmClick={handleDeleteConfirmed}
                onCancelClick={() => setConfirmDeleteModalVisible(false)}
            />
            <ErrorModal
                visible={errorModalVisible}
                errorText={error?.toString()}
                onCloseClick={handleError}
            />
            <FullScreenLoadingIndicator visible={loading} />
        </Card>
        </>
    )
}

export default Deviations

const useStyles = createUseStyles({
    card: {
        marginBottom: 20
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        marginBottom: 20,
        gap: 10
    },
    filterContainer: {
        marginBottom: 20,
    },
    button: {
        alignSelf: 'flex-end'
    }
})
